import React, { useState } from 'react';
import { Box, Card, TextField, Button, Typography, Container, Alert, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    
    // Priority: State (from OTP screen) > Query Param (direct link)
    const token = location.state?.token || searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);
    const dispatch = useDispatch();
    const { isLoading, error } = useSelector((state) => state.auth);

    const handleClickShowPassword = () => setShowPassword(!showPassword);

    const validatePassword = (password) => {
        if (password.length < 8) return 'Password must be at least 8 characters.';
        if (!/[A-Z]/.test(password)) return 'Password must contain an uppercase letter.';
        if (!/[a-z]/.test(password)) return 'Password must contain a lowercase letter.';
        if (!/[0-9]/.test(password)) return 'Password must contain a number.';
        if (!/[^A-Za-z0-9]/.test(password)) return 'Password must contain a special character.';
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setValidationError('');

        if (password !== confirmPassword) {
            setValidationError('Passwords do not match.');
            return;
        }

        const pwError = validatePassword(password);
        if (pwError) {
            setValidationError(pwError);
            return;
        }

        const result = await dispatch.auth.resetPasswordAsync({ token, password });
        if (result?.success) {
            setResetSuccess(true);
        }
    };

    if (!token) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'primary.dark' }}>
                <Container maxWidth="xs">
                    <Card sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            Invalid or missing reset token. Please request a new password reset link.
                        </Alert>
                        <Button fullWidth variant="outlined" component={Link} to="/forgot-password">
                            Request New Link
                        </Button>
                    </Card>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'primary.dark' }}>
            <Container maxWidth="xs">
                <Card sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                            Reset Password
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Enter and confirm your new password.
                        </Typography>
                    </Box>

                    {resetSuccess ? (
                        <Box>
                            <Alert severity="success" sx={{ mb: 3 }}>
                                Your password has been reset successfully. You can now sign in with your new password.
                            </Alert>
                            <Button fullWidth variant="contained" component={Link} to="/login">
                                Go to Login
                            </Button>
                        </Box>
                    ) : (
                        <>
                            {(error || validationError) && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {validationError || error}
                                </Alert>
                            )}
                            <form onSubmit={handleSubmit}>
                                <TextField
                                    fullWidth
                                    label="New Password"
                                    variant="outlined"
                                    type={showPassword ? 'text' : 'password'}
                                    margin="normal"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoFocus
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword}
                                                    edge="end"
                                                >
                                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    label="Confirm Password"
                                    variant="outlined"
                                    type={showPassword ? 'text' : 'password'}
                                    margin="normal"
                                    required
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={handleClickShowPassword}
                                                    edge="end"
                                                >
                                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Button
                                    fullWidth
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    color="primary"
                                    disabled={isLoading || !password || !confirmPassword}
                                    sx={{ mt: 1 }}
                                >
                                    {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
                                </Button>
                            </form>
                        </>
                    )}
                </Card>
            </Container>
        </Box>
    );
}
